import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Avatar,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Person as PersonIcon,
  History as HistoryIcon,
  TrendingUp as TrendingIcon,
  EmojiObjects as SuggestionsIcon,
  Close as CloseIcon,
  Star as StarIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import CustomerDataTab from "./CustomerDataTab";
import CustomerStatsTab from "./CustomerStatsTab";
import PurchaseHistoryTab from "./PurchaseHistoryTab";
import SuggestionsTab from "./SuggestionsTab";

import {
  getCustomer,
  getCustomerByContactId,
  getCustomerByWhatsapp,
  getCustomerStats,
  getCustomerPurchaseHistory,
  getCustomerSuggestions,
} from "../../services/pharmaApi";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minHeight: "70vh",
    maxHeight: "90vh",
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: theme.spacing(1),
  },
  titleContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  customerAvatar: {
    width: 48,
    height: 48,
    backgroundColor: theme.palette.primary.main,
  },
  customerInfo: {
    display: "flex",
    flexDirection: "column",
  },
  customerName: {
    fontWeight: 600,
    fontSize: "1.1rem",
  },
  customerMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  returningCustomerBadge: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
    fontWeight: 600,
  },
  newCustomerBadge: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.dark,
  },
  vipBadge: {
    backgroundColor: "#FFD700",
    color: "#333",
    fontWeight: 600,
  },
  tabsContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tab: {
    minWidth: 120,
    textTransform: "none",
  },
  tabIcon: {
    marginBottom: "0 !important",
    marginRight: theme.spacing(1),
  },
  tabPanel: {
    padding: theme.spacing(2),
    height: "calc(100% - 48px)",
    overflow: "auto",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 200,
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customer-tabpanel-${index}`}
      aria-labelledby={`customer-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const CustomerProfileModal = ({
  open,
  onClose,
  contact, // Whaticket contact object
  customerId, // PharmaFlow customer ID (optional, if already known)
  storeId,
  onAddToCart, // Callback to add product to cart
  onCustomerUpdate, // Callback when customer data is updated (e.g., address added)
}) => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);

  // Customer data
  const [customer, setCustomer] = useState(null);
  const [stats, setStats] = useState(null);
  const [purchaseHistory, setPurchaseHistory] = useState(null);
  const [suggestions, setSuggestions] = useState(null);

  // Pagination for purchase history
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Load customer data
  const loadCustomerData = useCallback(async () => {
    if (!open) return;

    setLoading(true);
    try {
      let customerData = null;

      // Try to get customer by ID first
      if (customerId) {
        try {
          customerData = await getCustomer(customerId);
        } catch (e) {
          console.log("Customer not found by ID:", customerId);
        }
      }

      // Try to get customer by Whaticket Contact ID
      if (!customerData && contact?.id) {
        try {
          customerData = await getCustomerByContactId(contact.id);
        } catch (e) {
          // Customer not found by contact ID
          console.log("Customer not found by contactId:", contact.id);
        }
      }

      // Fallback: Try to get customer by WhatsApp number
      if (!customerData && contact?.number) {
        try {
          // Clean the phone number (remove non-digits)
          const cleanNumber = contact.number.replace(/\D/g, "");
          customerData = await getCustomerByWhatsapp(cleanNumber);
          console.log("Customer found by whatsapp:", cleanNumber, customerData);
        } catch (e) {
          // Customer not found by whatsapp
          console.log("Customer not found by whatsapp:", contact.number);
        }
      }

      if (customerData) {
        setCustomer(customerData);

        // Load stats in parallel
        try {
          const statsData = await getCustomerStats(customerData.id);
          setStats(statsData);
        } catch (e) {
          console.error("Error loading customer stats:", e);
          setStats(null);
        }
      } else {
        // Create a minimal customer object from contact
        setCustomer({
          id: null,
          name: contact?.name || "Cliente",
          whatsapp: contact?.number || "",
          whaticketContactId: contact?.id,
          isNew: true,
        });
        setStats(null);
      }
    } catch (error) {
      console.error("Error loading customer data:", error);
      toast.error("Erro ao carregar dados do cliente");
    } finally {
      setLoading(false);
    }
  }, [open, customerId, contact]);

  // Load purchase history when tab is selected
  const loadPurchaseHistory = useCallback(async (page = 1) => {
    if (!customer?.id) return;

    setHistoryLoading(true);
    try {
      const historyData = await getCustomerPurchaseHistory(customer.id, {
        page,
        limit: 20,
      });
      setPurchaseHistory(historyData);
      setHistoryPage(page);
    } catch (error) {
      console.error("Error loading purchase history:", error);
    } finally {
      setHistoryLoading(false);
    }
  }, [customer?.id]);

  // Load suggestions when tab is selected
  const loadSuggestions = useCallback(async () => {
    if (!customer?.id) return;

    try {
      const suggestionsData = await getCustomerSuggestions(customer.id, {
        storeId,
        limit: 15,
      });
      setSuggestions(suggestionsData);
    } catch (error) {
      console.error("Error loading suggestions:", error);
    }
  }, [customer?.id, storeId]);

  useEffect(() => {
    loadCustomerData();
  }, [loadCustomerData]);

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 2 && !purchaseHistory && customer?.id) {
      loadPurchaseHistory(1);
    } else if (activeTab === 3 && !suggestions && customer?.id) {
      loadSuggestions();
    }
  }, [activeTab, customer?.id, purchaseHistory, suggestions, loadPurchaseHistory, loadSuggestions]);

  const handleClose = () => {
    setActiveTab(0);
    setCustomer(null);
    setStats(null);
    setPurchaseHistory(null);
    setSuggestions(null);
    setHistoryPage(1);
    onClose();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAddToCart = (product) => {
    if (onAddToCart) {
      onAddToCart(product);
      // Toast is shown by parent (TicketPdvPanel.handleAddToCart)
    }
  };

  const getCustomerBadge = () => {
    if (!stats) return null;

    if (stats.totalOrders === 0) {
      return <Chip size="small" label="Novo Cliente" className={classes.newCustomerBadge} />;
    }

    if (stats.totalSpent >= 1000 || stats.totalOrders >= 10) {
      return (
        <Chip
          size="small"
          icon={<StarIcon style={{ color: "#333" }} />}
          label="Cliente VIP"
          className={classes.vipBadge}
        />
      );
    }

    return (
      <Chip
        size="small"
        label={`${stats.totalOrders} pedido${stats.totalOrders > 1 ? "s" : ""}`}
        className={classes.returningCustomerBadge}
      />
    );
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box className={classes.titleContent}>
          <Avatar className={classes.customerAvatar}>
            {customer ? getInitials(customer.name) : <PersonIcon />}
          </Avatar>
          <Box className={classes.customerInfo}>
            <Typography className={classes.customerName}>
              {loading ? "Carregando..." : customer?.name || "Perfil do Cliente"}
            </Typography>
            <Box className={classes.customerMeta}>
              {!loading && getCustomerBadge()}
              {customer?.whatsapp && (
                <Typography variant="caption" color="textSecondary">
                  {customer.whatsapp}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        <IconButton className={classes.closeButton} onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box className={classes.tabsContainer}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            className={classes.tab}
            icon={<PersonIcon className={classes.tabIcon} />}
            label="Dados"
          />
          <Tab
            className={classes.tab}
            icon={<TrendingIcon className={classes.tabIcon} />}
            label="Estatisticas"
            disabled={!customer?.id}
          />
          <Tab
            className={classes.tab}
            icon={<HistoryIcon className={classes.tabIcon} />}
            label="Historico"
            disabled={!customer?.id}
          />
          <Tab
            className={classes.tab}
            icon={<SuggestionsIcon className={classes.tabIcon} />}
            label="Sugestoes"
            disabled={!customer?.id}
          />
        </Tabs>
      </Box>

      <DialogContent className={classes.tabPanel}>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TabPanel value={activeTab} index={0}>
              <CustomerDataTab
                customer={customer}
                contact={contact}
                onCustomerUpdate={() => {
                  loadCustomerData();
                  // Also notify parent to refresh customer data
                  if (onCustomerUpdate) onCustomerUpdate();
                }}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <CustomerStatsTab stats={stats} customer={customer} />
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <PurchaseHistoryTab
                purchaseHistory={purchaseHistory}
                loading={historyLoading}
                page={historyPage}
                onPageChange={loadPurchaseHistory}
                onAddToCart={handleAddToCart}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={3}>
              <SuggestionsTab
                suggestions={suggestions}
                onAddToCart={handleAddToCart}
                storeId={storeId}
              />
            </TabPanel>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary" variant="contained">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerProfileModal;
